import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Form, Row, Col, Button, Container, Spinner } from "react-bootstrap";
import "./WorkOrderUpdate.css";
import { toast } from "react-toastify";
import WorkOrderAddress from "./WorkOrderAddress";

import { useProperty } from "../../data/PropertyHook";
import WorkOrderEntry from "./entry/WorkOrderEntry";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import useLogger from "../../util/Logger";
import { useAppointmentSlots } from "../../data/AppointmentSlotHook";
import { useWorkItemAgreements } from "../../data/WorkItemAgreementsHook";
import { useWorkItemCategories } from "../../data/WorkItemCategoriesHook";
import { useTypeOfWork } from "../../data/TypeOfWorkHook";
import { useAssets } from "../../data/AssetsHook";
import { useCategories } from "../../data/CategoriesHook";
import { useSubCategories } from "../../data/SubCategoriesHook";
import { useReporterType } from "../../data/ReporterTypeHook";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import { isWorkOrderClosed } from "../../data/WorkOrderStatus";
import SpinnerButton from "../shared/Spinnerbutton";

function WorkOrderUpdate() {
  const { propertyId, workOrderId } = useParams();
  const propertyRepository = useProperty();
  const [property, setProperty] = useState(null);
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const workOrdersRepository = useWorkOrders();
  const logger = useLogger();
  const appointmentSlotsRepository = useAppointmentSlots();
  const workItemAgreementsRepository = useWorkItemAgreements();
  const workItemCategoryRepository = useWorkItemCategories();
  const typeOfWorkRepository = useTypeOfWork();
  const assetRepository = useAssets();
  const categoryRepository = useCategories();
  const subCategoryRepository = useSubCategories();
  const reporterTypeRepository = useReporterType();
  const breadcrumbRepository = useBreadcrumb();
  const navigate = useNavigate();

  useEffect(() => {
    const runMe = async () => {
      try {
        logger.debug("WorkOrderUpdate.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
          {
            route: `/property/${propertyId}/workorder/${workOrderId}`,
            name: "work order",
          },
          {
            route: `/property/${propertyId}/workOrder/${workOrderId}/update`,
            name: "update",
          },
        ]);

        const [retrievedProperty, workItemCategories, reporterTypes] =
          await Promise.all([
            propertyRepository.getProperty(propertyId),
            workItemCategoryRepository.updateWorkItemCategories(),
            reporterTypeRepository.updateReporterTypes(),
            appointmentSlotsRepository.updateAppointmentSlots(),
            typeOfWorkRepository.updateTypeOfWorks(),
            workItemAgreementsRepository.updateWorkItemAgreements(),
            assetRepository.updateAssets(propertyId),
          ]);

        setProperty(retrievedProperty);

        const retrievedWorkOrder =
          await workOrdersRepository.getWorkOrderForUpdate(
            propertyId,
            workOrderId,
            workItemCategories
          );
        if (!retrievedWorkOrder)
          throw new Error(
            `Failed to load work order for property '${retrievedProperty.id}' and work order '${workOrderId}'`
          );

        const categories = await categoryRepository.getCategories(
          retrievedWorkOrder.type
        );

        if (!categories) throw new Error(`No categories were retrieved`);
        const subCategories = await subCategoryRepository.updateSubCategories(
          retrievedWorkOrder.category
        );
        if (!subCategories) throw new Error(`No subCategories were retrieved`);

        if (isWorkOrderClosed(retrievedWorkOrder)) {
          throw new Error(
            `Illegal attempt to edit closed work order for property '${retrievedProperty.id}' and work order '${workOrderId}'`
          );
        }

        // Fix for some test data
        if (!retrievedWorkOrder.asset) {
          retrievedWorkOrder.asset = "Unknown";
        }
        if (retrievedWorkOrder.reportertype === null) {
          retrievedWorkOrder.reportertype = reporterTypes[0].id;
        }

        // Load entry from
        workOrdersRepository.setEntry(retrievedWorkOrder);

        setIsLoading(false);
      } catch (err) {
        logger.error(
          "WorkOrderUpdate.useEffect",
          `An error occurred while loading work order '${workOrderId}' ${err}`
        );
        toast.error(
          "An error occurred while loading the work order. Please try again."
        );
        navigate("/");
      }
    };
    runMe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event) => {
    try {
      setIsSaving(true);

      const form = event.currentTarget;
      event.preventDefault();
      event.stopPropagation();

      if (form.checkValidity() === false) {
        setValidated(true);
        toast.error(
          "There are errors on your form. Please check each field and try again."
        );
      } else {
        // Save the work order
        const current = workOrdersRepository.getEntry();
        await workOrdersRepository.updateWorkOrder(current);
        toast.success("The Work Order was updated successfully.");
        navigate(`/property/${propertyId}/workOrder/${workOrderId}`);
      }
    } catch (err) {
      logger.error(
        "WorkOrderUpdate.saveWorkOrder",
        `API patch work order failed ${err}`
      );
      toast.error("The work order could not be updated. Please try again.");
    }
    setIsSaving(false);
  };

  const currentEntry = workOrdersRepository.getEntry();

  // We need to wait for the base data for the entry to be set (siteId, territoryId etc)
  // before we load the sub-components as they rely on this data being set. If we
  // don't check this here, the sub-components may call getEntry() before setEntry()
  // gets called, and our base data gets overwritten with nulls.
  return !isLoading && currentEntry ? (
    <Container className="coreContent">
      <h2>Update Work Order</h2>
      <WorkOrderAddress property={property} />
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <div className="entry-container">
          <WorkOrderEntry mode="update" />
        </div>

        <div className="work-order-update-buttons-container">
          <Row xs="auto">
            <Col className="work-order-update-button">
              <Link to={`/property/${property.id}/workorder/${workOrderId}`}>
                <Button variant="secondary">Back to view</Button>
              </Link>
            </Col>
            <Col>
              <SpinnerButton
                text="Save"
                variant="primary"
                action={() => document.getElementById("updateSubmit").click()}
                waiting={isSaving}
              />
            </Col>
            <Col>
              <Button
                id="updateSubmit"
                type="submit"
                style={{ visibility: "hidden" }}
              >
                Save
              </Button>
            </Col>
          </Row>
        </div>
      </Form>
    </Container>
  ) : (
    <Container className="coreContent">
      <Spinner
        animation="border"
        role="status"
        style={{
          marginTop: "200px",
          marginLeft: "50%",
          marginBottom: "200px",
        }}
      >
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </Container>
  );
}

export default WorkOrderUpdate;
